import type { QueryClient } from '@tanstack/react-query';
import { ClubService } from '@/services/club.service';
import { ProfessionalsService } from '@/services/professional.service';
import { PublicService } from '@/services/public.service';
import { ServiceService } from '@/services/service.service';
import { SessionService } from '@/services/session.service';
import { resolveSchedulingHistoryForClubUpsell } from './schedulingPendingSession';

const CHECKOUT_CACHE_STALE_MS = 60_000;

/** Mesma chave de `useGetSessions({ limit: 1, page: 1, isMe: true })`. */
export const paymentCheckoutSessionsQueryKey = [
	'sessions',
	undefined,
	undefined,
	undefined,
	undefined,
	1,
	1,
	undefined,
	undefined,
] as const;

export type PaymentCheckoutWarmHints = {
	professionalId?: string;
	serviceId?: string;
	/** Sessão atual no checkout; vazio enquanto o usuário ainda não reservou. */
	sessionId?: string;
};

function maybePrefetchClubsAvailable(
	queryClient: QueryClient,
	serviceId: string,
	sessionId: string,
): void {
	if (!serviceId) return;

	const myClub = queryClient.getQueryData<{
		subscription?: { status?: string };
	}>(['clubs', 'me']);
	if (myClub?.subscription?.status === 'ACTIVE') return;

	const mySessionsPage =
		queryClient.getQueryData<Sessions.GetSessionsRespose>([
			...paymentCheckoutSessionsQueryKey,
		]);
	if (mySessionsPage === undefined) return;

	let shouldPrefetchUpsell = false;
	if (sessionId) {
		shouldPrefetchUpsell =
			resolveSchedulingHistoryForClubUpsell(
				mySessionsPage,
				true,
				sessionId,
			) === true;
	} else {
		shouldPrefetchUpsell = (mySessionsPage.total ?? 0) > 1;
	}

	if (!shouldPrefetchUpsell) return;

	void queryClient.prefetchQuery({
		queryKey: ['clubs', 'available'],
		queryFn: () => ClubService.getAvailable(),
		staleTime: CHECKOUT_CACHE_STALE_MS,
	});
}

/**
 * Dispara pré-carregamento em background (não bloqueia abertura do modal).
 * Deve ser chamado cedo (passo 2 do agendamento, home) e ao abrir o checkout.
 */
export type WarmPaymentCheckoutCacheOptions = {
	/** Clube/histórico exigem sessão autenticada; guest na revisão não deve disparar 401. */
	includeUserScoped?: boolean;
};

export function warmPaymentCheckoutCache(
	queryClient: QueryClient,
	hints: PaymentCheckoutWarmHints,
	options?: WarmPaymentCheckoutCacheOptions,
): void {
	const professionalId = hints.professionalId ?? '';
	const serviceId = hints.serviceId ?? '';
	const sessionId = hints.sessionId ?? '';
	const includeUserScoped = options?.includeUserScoped !== false;

	const prefetchOpts = { staleTime: CHECKOUT_CACHE_STALE_MS };

	if (includeUserScoped) {
		void queryClient.prefetchQuery({
			queryKey: ['clubs', 'me'],
			queryFn: () => ClubService.getMe(),
			...prefetchOpts,
		});

		void queryClient
			.prefetchQuery({
				queryKey: [...paymentCheckoutSessionsQueryKey],
				queryFn: () => SessionService.getMe({ limit: 1, page: 1 }),
				...prefetchOpts,
			})
			.then(() => {
				maybePrefetchClubsAvailable(queryClient, serviceId, sessionId);
			})
			.catch(() => {});

		maybePrefetchClubsAvailable(queryClient, serviceId, sessionId);
	}

	if (professionalId) {
		if (includeUserScoped) {
			void queryClient.prefetchQuery({
				queryKey: ['professional', professionalId],
				queryFn: () => ProfessionalsService.getById(professionalId),
				...prefetchOpts,
			});
		} else {
			void queryClient.prefetchQuery({
				queryKey: ['professional-public', professionalId],
				queryFn: () =>
					PublicService.getProfessionalPublicById(professionalId),
				...prefetchOpts,
			});
		}
	}

	if (serviceId && includeUserScoped) {
		void queryClient.prefetchQuery({
			queryKey: ['service', serviceId],
			queryFn: () => ServiceService.getById(serviceId),
			...prefetchOpts,
		});
	}
}

/** @deprecated Prefer `warmPaymentCheckoutCache` (não bloqueia UI). */
export function warmPaymentCheckoutCacheFromSession(
	queryClient: QueryClient,
	session: Pick<Sessions.Session, 'id' | 'service_id' | 'professional_id'>,
): void {
	warmPaymentCheckoutCache(queryClient, {
		sessionId: session.id,
		serviceId: session.service_id ?? '',
		professionalId: session.professional_id ?? '',
	});
}
